import { Card, CardBody, CardHeader, CardTitle, StackLayout, TileLayout } from '@progress/kendo-react-layout';
import { useEffect, useMemo, useState } from 'react';
import { useLocalization } from '@progress/kendo-react-intl';
import { DevicesKpiCard } from '../components/dashboard/DevicesKpiCard';
import { AlarmsKpiCard } from '../components/dashboard/AlarmsKpiCard';
import { NearmissKpiCard } from '../components/dashboard/NearmissKpiCard';
import { BeaconsKpiCard } from '../components/dashboard/BeaconsKpiCard';
import { Alarms } from './Alarms';
import { AccessToken } from '../config/AccessToken';
import axios from 'axios';

type DashboardTile = {
  id: number;
  defaultPosition: { col: number; colSpan: number; rowSpan: number; order: number };
  header: string;
  body: JSX.Element;
  resizable: boolean;
  visible: boolean;
};

export const Dashboard = (() => {
  const COMPACT_LAYOUT_BREAKPOINT = 1200;

  const accountId = localStorage.getItem('accountId');
  const languageId = localStorage.getItem('languageId');
  const SERVER_URL = process.env.REACT_APP_SERVER_URL;
  const [instance, accessTokenRequest] = AccessToken();

  const [error, setError] = useState('');

  const localizationService = useLocalization();

  const userEmail = localStorage.getItem('email');

  const [isCompactLayout, setIsCompactLayout] = useState(window.innerWidth < COMPACT_LAYOUT_BREAKPOINT);

  const initialTiles: DashboardTile[] = useMemo(
    () => [
      {
        id: 0,
        defaultPosition: { col: 1, colSpan: 4, rowSpan: 2, order: 1 },
        header: localizationService.toLanguageString('dashboard.widget.devices.title', 'NOT_FOUND'),
        body:
          <DevicesKpiCard />,
        resizable: false,
        visible: true,
      },
      {
        id: 5,
        defaultPosition: { col: 5, colSpan: 4, rowSpan: 2, order: 2 },
        header: localizationService.toLanguageString('beacons.title', 'NOT_FOUND'),
        body:
          <BeaconsKpiCard />,
        resizable: false,
        visible: true
      },
      {
        id: 2,
        defaultPosition: { col: 1, colSpan: 4, rowSpan: 2, order: 3 },
        header: localizationService.toLanguageString('alarms.title', 'NOT_FOUND'),
        body:
          <AlarmsKpiCard />,
        resizable: false,
        visible: true
      },
      {
        id: 6,
        defaultPosition: { col: 5, colSpan: 4, rowSpan: 2, order: 4 },
        header: localizationService.toLanguageString('dashboard.widget.nearmiss.title', 'Nearmiss'),
        body:
          <NearmissKpiCard />,
        resizable: false,
        visible: true
      },
    ],
    [localizationService]
  );

  const [tiles, setTiles] = useState<DashboardTile[]>(() => {
    return initialTiles;
  });

  useEffect(() => {
    const tileStateToSave = tiles.map(({ id, header, defaultPosition, resizable, visible }) => ({
      id,
      header,
      defaultPosition,
      resizable,
      visible,
    }));
    localStorage.setItem(`tileState: ${userEmail}`, JSON.stringify(tileStateToSave));
  }, [tiles, userEmail]);

  useEffect(() => {
    setTiles(initialTiles);
  }, [initialTiles]);

  useEffect(() => {
    const onResize = () => {
      setIsCompactLayout(window.innerWidth < COMPACT_LAYOUT_BREAKPOINT);
    };

    window.addEventListener('resize', onResize, false);
    onResize();
    return () => {
      window.removeEventListener('resize', onResize, false);
    };
  }, []);

  const updateClientConfig = async () => {
    if (!SERVER_URL || !accountId) {
      return;
    }

    const params = {
      settings: [{
        id: 'Language',
        group: 'VIE',
        description: 'Smart Locator Language',
        value: languageId === 'it-IT' ? '1' : '0',
        clientId: Number(accountId) ?? ''
      }]
    };

    const updateClientLanguageSetting = async (accessToken: any) => {
      const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };

      try {
        await axios.put(`${SERVER_URL}/settings/Client`, params, { headers });
      } catch (error: any) {
        console.error("Errore:", error);
      }
    };

    const storedAccessToken = localStorage.getItem('accessToken');
    if (storedAccessToken) {
      await updateClientLanguageSetting(storedAccessToken);
    } else {
      await instance.initialize();
      const accessTokenResponse: any = await instance.acquireTokenSilent(accessTokenRequest);
      await updateClientLanguageSetting(accessTokenResponse.accessToken);
    }
  };

  useEffect(() => {
    void updateClientConfig();
  }, []);

  const dashboardTitle = localizationService.toLanguageString('dashboard.title', 'NOT_FOUND');
  const lastActiveAlarmsTitle = localizationService.toLanguageString('dashboard.last_active_alarms', 'NOT_FOUND');
  const todayLabel = new Date().toLocaleDateString(languageId === 'it-IT' ? 'it-IT' : 'en-US', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
  const dashboardColors = {
    pageBackground:
      'radial-gradient(circle at 10% 2%, rgba(0, 107, 128, 0.19), transparent 34%), radial-gradient(circle at 88% 1%, rgba(230, 110, 44, 0.16), transparent 30%), linear-gradient(180deg, #eaf3f7 0%, #e3eef4 100%)',
    cardBorder: '#b9d0da',
    cardShadow: '0 14px 36px rgba(5, 72, 96, 0.14)',
    cardHeaderBorder: '#d5e3e9',
    cardHeaderPrimaryBackground:
      'linear-gradient(112deg, rgba(7, 105, 130, 0.2) 0%, rgba(45, 149, 173, 0.12) 42%, rgba(255, 255, 255, 0.85) 100%)',
    cardHeaderAlertBackground:
      'linear-gradient(112deg, rgba(217, 90, 25, 0.2) 0%, rgba(240, 137, 75, 0.12) 42%, rgba(255, 255, 255, 0.85) 100%)',
    cardHeaderPrimaryAccent: '#0c6884',
    cardHeaderAlertAccent: '#d56323',
    title: '#0b4f64',
    titleAlert: '#8e3f17',
    tileHeaderText: '#f1fbff',
    tileHeaderHighlight: 'linear-gradient(90deg, rgba(10, 94, 120, 0.95) 0%, rgba(18, 122, 145, 0.9) 100%)',
    tileText: '#105f77'
  };

  const getTileHeaderFontSize = (label: string): string => {
    const length = label.length;

    if (length > 44) {
      return isCompactLayout ? '0.58rem' : '0.66rem';
    }
    if (length > 34) {
      return isCompactLayout ? '0.62rem' : '0.66rem';
    }
    if (length > 26) {
      return isCompactLayout ? '0.68rem' : '0.66rem';
    }

    return isCompactLayout ? '0.74rem' : '0.66rem';
  };

  const tileFocusResetCss = `
    .dashboard-tile-layout .k-tilelayout-item:focus,
    .dashboard-tile-layout .k-tilelayout-item:focus-visible,
    .dashboard-tile-layout .k-tilelayout-item.k-focus,
    .dashboard-tile-layout .k-tilelayout-item:focus-within {
      outline: none !important;
      box-shadow: none !important;
      border-color: var(--kendo-color-border, rgba(33, 37, 41, 0.13));
    }
  `;

  const tileLayoutItems = useMemo(
    () =>
      tiles
        ?.filter((tile) => tile.visible)
        ?.map((tile) => ({
          id: tile.id,
          header: (
            <span
              style={{
                display: 'inline-block',
                maxWidth: '100%',
                color: dashboardColors.tileHeaderText,
                fontWeight: 700,
                fontSize: '0.875rem',
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
              }}
            >
              {tile.header}
            </span>
          ),
          defaultPosition: isCompactLayout
            ? {
                col: 1,
                colSpan: 1,
                rowSpan: tile.defaultPosition.rowSpan,
                order: tile.defaultPosition.order,
              }
            : tile.defaultPosition,
          body: tile.body,
          resizable: tile.resizable
        })),
    [dashboardColors.tileHeaderHighlight, dashboardColors.tileHeaderText, isCompactLayout, tiles]
  );

  return (
    <div
      style={{
        background: dashboardColors.pageBackground,
        minHeight: '100%',
        padding: '1rem 0.8rem 1.25rem',
        overflowY: 'visible'
      }}
    >
      <style>{tileFocusResetCss}</style>
      {error && <p className="text-danger" style={{ zIndex: 999, position: 'relative', width: '65%' }}>{error}</p>}

      <StackLayout orientation="vertical" gap={16}>
        <Card
          style={{
            borderRadius: '16px',
            border: `1px solid ${dashboardColors.cardBorder}`,
            backgroundColor: '#ffffff',
            boxShadow: dashboardColors.cardShadow,
            overflow: 'hidden'
          }}
        >
          <CardHeader
            style={{
              borderBottom: `1px solid ${dashboardColors.cardHeaderBorder}`,
              borderLeft: `6px solid ${dashboardColors.cardHeaderPrimaryAccent}`,
              padding: '0.92rem 1rem 0.74rem',
              background: dashboardColors.cardHeaderPrimaryBackground,
              boxShadow: 'inset 0 -1px 0 rgba(255, 255, 255, 0.65)'
            }}
          >
            <StackLayout orientation="horizontal" align={{ horizontal: 'stretch', vertical: 'middle' }}>
              <CardTitle className="page-title" style={{ margin: 0, color: dashboardColors.title, letterSpacing: '0.03em' }}>
                {dashboardTitle}
              </CardTitle>              
            </StackLayout>
          </CardHeader>
          <CardBody style={{ padding: '0.8rem' }}>
            <div
              style={{
                height: '6px',
                borderRadius: '999px',
                marginBottom: '0.75rem',
                background: 'linear-gradient(90deg, #0f6e8a 0%, #1e9cb3 42%, #f08a43 100%)',
                opacity: 0.9
              }}
            />
            <TileLayout
              className="dashboard-tile-layout"
              columns={isCompactLayout ? 1 : 8}
              items={tileLayoutItems}
              rowHeight={170}
              gap={isCompactLayout ? { rows: 12, columns: 0 } : { rows: 12, columns: 8 }}
              ignoreDrag={() => true}
              style={{ backgroundColor: 'transparent', color: dashboardColors.tileText, overflow: 'visible' }}
            />
          </CardBody>
        </Card>

        <Card
          style={{
            borderRadius: '16px',
            border: `1px solid ${dashboardColors.cardBorder}`,
            backgroundColor: '#ffffff',
            boxShadow: dashboardColors.cardShadow,
            overflow: 'hidden'
          }}
        >
          <CardHeader
            style={{
              borderBottom: `1px solid ${dashboardColors.cardHeaderBorder}`,
              borderLeft: `6px solid ${dashboardColors.cardHeaderAlertAccent}`,
              padding: '0.92rem 1rem 0.74rem',
              background: dashboardColors.cardHeaderAlertBackground,
              boxShadow: 'inset 0 -1px 0 rgba(255, 255, 255, 0.65)'
            }}
          >
            <CardTitle className="section-title" style={{ margin: 0, color: dashboardColors.titleAlert, letterSpacing: '0.03em' }}>
              {lastActiveAlarmsTitle}
            </CardTitle>
          </CardHeader>
          <CardBody style={{ padding: '0.8rem' }}>
            <div
              style={{
                height: '6px',
                borderRadius: '999px',
                marginBottom: '0.75rem',
                background: 'linear-gradient(90deg, #e66e2c 0%, #f19a61 45%, #ffd5ba 100%)',
                opacity: 0.85
              }}
            />
            <Alarms preview='preview' />
          </CardBody>
        </Card>
      </StackLayout>
    </div>
  );
});

